import React, { forwardRef, MouseEvent } from 'react';
import { Checkbox, Stack, Typography } from '@mui/material';
import {
  TreeItem as MuiTreeItem,
  TreeItemProps,
  useTreeItem,
} from '@mui/x-tree-view';
import { themePalette } from '@theme/muiTheme';
import { DropdownIcon } from '@utils/iconsDefs';
import { TreeCheckState } from '@views/Project/helpers';

export type SelectHandlerOptions = {
  nodeId: string;
  checked: boolean;
  parentsId: string[];
};

export type SelectHandler = (options: SelectHandlerOptions) => void;

const CustomContent = forwardRef(function CustomContent(
  props: Pick<TreeItemProps, 'nodeId' | 'label' | 'sx' | 'disabled'> & {
    onSelect: (ops: Omit<SelectHandlerOptions, 'parentsId'>) => void;
    onClick: () => void;
    hasChildren?: boolean;
    checkState?: TreeCheckState;
  },
  ref,
) {
  const {
    label,
    nodeId,
    onSelect,
    hasChildren,
    onClick,
    sx,
    disabled,
    checkState,
  } = props;

  const { selected, expanded, preventSelection } = useTreeItem(nodeId);

  const isChecked = checkState ? checkState === 'checked' : selected;

  const handleExpansionClick = (event: MouseEvent<HTMLDivElement>) => {
    preventSelection(event);
    onClick();
  };

  const handleSelectChange = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onSelect({ nodeId, checked: !isChecked });
  };

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        cursor: 'pointer',
        '&:hover': { backgroundColor: themePalette.grey[400] },
        ...sx,
      }}
      p="16px 24px"
      onClick={handleExpansionClick}
      ref={ref as React.Ref<HTMLDivElement>}
    >
      <Stack direction="row" alignItems="center" gap={2}>
        <Checkbox
          checked={isChecked}
          indeterminate={checkState === 'indeterminate'}
          onClick={handleSelectChange}
          disabled={disabled}
        />

        <Typography variant="body1">{label}</Typography>
      </Stack>

      {hasChildren && (
        <DropdownIcon
          fill={themePalette.grey[1000]}
          style={{ transform: expanded ? 'rotate(180deg)' : '', minWidth: 20 }}
        />
      )}
    </Stack>
  );
});

const TreeItem = forwardRef(function CustomTreeItem(
  {
    onSelect,
    onClick,
    hasChildren,
    checkState,
    sx,
    ...props
  }: Pick<
    TreeItemProps,
    'nodeId' | 'label' | 'children' | 'sx' | 'disabled'
  > & {
    onSelect: (ops: Omit<SelectHandlerOptions, 'parentsId'>) => void;
    onClick: () => void;
    hasChildren?: boolean;
    checkState?: TreeCheckState;
  },
  ref: React.Ref<HTMLLIElement>,
) {
  return (
    <MuiTreeItem
      // eslint-disable-next-line react/no-unstable-nested-components
      ContentComponent={(innerProps) => (
        <CustomContent
          onSelect={onSelect as any}
          hasChildren={hasChildren}
          checkState={checkState}
          {...innerProps}
          onClick={onClick as any}
          sx={sx}
          disabled={props.disabled}
        />
      )}
      {...props}
      ref={ref}
    />
  );
});

export default TreeItem;
